// LoadingScreen — a single, consistent loading affordance used wherever the
// app has to wait for something. Two render modes:
//
//   mode="full"   — replaces the whole viewport. Used while auth is hydrating
//                   (AppShell) and on the post-OAuth callback (AuthCallback).
//                   The user has nothing to interact with yet, so a full takeover
//                   is the clearest signal.
//
//   mode="overlay" — translucent layer over the current page. Used by Login
//                    and Register while the auth round-trip is in flight, so
//                    the user can still see the form they just submitted (and
//                    can't double-submit, since the form is underneath a
//                    pointer-events: auto layer).
//
// Visual: a centred pulsing brand mark inside a soft ring, with an optional
// message underneath. Animation uses motion's <motion.span> so it integrates
// with the rest of the app's motion vocabulary (see AppShell, AuthCallback).

import { motion } from "motion/react";
import { BrandMark } from "./BrandMark";
import { cn } from "@/lib/utils";

type LoadingScreenProps = {
  /** "full" replaces the page; "overlay" sits on top of the page. */
  mode?: "full" | "overlay";
  /** Optional caption shown under the spinner. Defaults to "載入中…" (full) or "處理中…" (overlay). */
  message?: string;
  /** Optional override for the spinner size. */
  size?: "sm" | "md" | "lg";
  className?: string;
};

const sizeToBrand: Record<NonNullable<LoadingScreenProps["size"]>, "sm" | "md" | "lg"> = {
  sm: "sm",
  md: "md",
  lg: "lg",
};

export function LoadingScreen({
  mode = "full",
  message,
  size = "md",
  className,
}: LoadingScreenProps) {
  const caption =
    message ?? (mode === "full" ? "載入中…" : "處理中…");

  const spinner = (
    <div
      role="status"
      aria-live="polite"
      aria-busy="true"
      className={cn(
        "flex flex-col items-center justify-center gap-4",
        className,
      )}
    >
      {/* Pulsing ring around the brand mark — same hue family as the brand
          panel on Login/Register so the loading state visually belongs. */}
      <div className="relative">
        <motion.span
          aria-hidden
          className="absolute inset-0 rounded-2xl bg-primary/20"
          animate={{ scale: [1, 1.25, 1], opacity: [0.5, 0, 0.5] }}
          transition={{ duration: 1.6, ease: "easeInOut", repeat: Infinity }}
        />
        <motion.div
          animate={{ scale: [1, 1.04, 1] }}
          transition={{ duration: 1.6, ease: "easeInOut", repeat: Infinity }}
        >
          <BrandMark size={sizeToBrand[size]} />
        </motion.div>
      </div>
      <p className="text-sm font-medium text-foreground/80">{caption}</p>
    </div>
  );

  if (mode === "overlay") {
    return (
      <div
        // pointer-events-auto so the overlay genuinely blocks the form
        // beneath it; backdrop-blur to keep the form readable through it.
        className="fixed inset-0 z-50 flex items-center justify-center bg-background/70 backdrop-blur-sm"
      >
        {spinner}
      </div>
    );
  }

  // Full mode: own the viewport, same background tokens as the auth pages
  // so the brand colours stay consistent.
  return (
    <div className="flex min-h-screen items-center justify-center bg-background text-foreground">
      {spinner}
    </div>
  );
}

